import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function RoleManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  const [dashboard, setDashboard] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);

  const [roleForm, setRoleForm] = useState({ name: "", description: "", department: "", maxTransactionLimit: "" });
  const [deptForm, setDeptForm] = useState({ name: "", code: "", description: "" });
  const [assignForm, setAssignForm] = useState({ userId: "", roleId: "", isPrimary: false });
  const [profileForm, setProfileForm] = useState({ userId: "", departmentId: "", jobTitle: "", employeeId: "", branch: "", canApproveTransactions: false, approvalLimit: "" });
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const [deptFilter, setDeptFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  useEffect(() => { loadDashboard(); loadAllUsers(); }, []);

  useEffect(() => {
    if (activeTab === "roles") loadRoles();
    else if (activeTab === "departments") loadDepartments();
    else if (activeTab === "users") loadUsers();
    else if (activeTab === "permissions") loadPermissions();
  }, [activeTab, deptFilter, roleFilter]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get("/Roles/dashboard");
      setDashboard(res.data);
      await Promise.all([loadRoles(), loadDepartments()]);
    } catch (err) {
      console.error("Error:", err);
      if (err.response?.status === 403) navigate("/admin");
    } finally { setLoading(false); }
  };

  const loadRoles = async () => {
    try {
      const res = await api.get(`/Roles?department=${deptFilter}&isActive=true`);
      setRoles(res.data);
    } catch (err) { console.error("Error:", err); }
  };

  const loadDepartments = async () => {
    try {
      const res = await api.get("/Roles/departments?isActive=true");
      setDepartments(res.data);
    } catch (err) { console.error("Error:", err); }
  };

  const loadUsers = async () => {
    try {
      const res = await api.get(`/Roles/users?departmentId=${deptFilter}&roleId=${roleFilter}`);
      setUsers(res.data);
    } catch (err) { console.error("Error:", err); }
  };

  const loadAllUsers = async () => {
    try {
      const res = await api.get("/Admin/users");
      setAllUsers(res.data.filter(u => !u.isAdmin));
    } catch (err) { console.error("Error:", err); }
  };

  const loadPermissions = async () => {
    try {
      const res = await api.get("/Roles/permissions/grouped");
      setPermissions(res.data);
    } catch (err) { console.error("Error:", err); }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...roleForm,
        maxTransactionLimit: roleForm.maxTransactionLimit === "" ? null : parseFloat(roleForm.maxTransactionLimit),
        permissionIds: selectedPermissions
      };
      await api.post("/Roles", payload);
      alert("✅ Role created!");
      setShowRoleModal(false);
      setRoleForm({ name: "", description: "", department: "", maxTransactionLimit: "" });
      setSelectedPermissions([]);
      loadRoles();
      loadDashboard();
    } catch (err) { alert(err.response?.data?.message || "Failed"); }
  };

  const handleCreateDept = async (e) => {
    e.preventDefault();
    try {
      await api.post("/Roles/departments", deptForm);
      alert("✅ Department created!");
      setShowDeptModal(false);
      setDeptForm({ name: "", code: "", description: "" });
      loadDepartments();
      loadDashboard();
    } catch (err) { alert(err.response?.data?.message || "Failed"); }
  };

  const handleAssignRole = async (e) => {
    e.preventDefault();
    try {
      await api.post("/Roles/users/assign", assignForm);
      alert("✅ Role assigned!");
      setShowAssignRoleModal(false);
      setAssignForm({ userId: "", roleId: "", isPrimary: false });
      loadUsers();
      loadDashboard();
    } catch (err) { alert(err.response?.data?.message || "Failed"); }
  };

  const handleRemoveRole = async (userId, roleId) => {
    if (!window.confirm("Remove this role?")) return;
    try {
      await api.delete(`/Roles/users/${userId}/roles/${roleId}`);
      alert("✅ Role removed!");
      loadUsers();
    } catch (err) { alert(err.response?.data?.message || "Failed"); }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      // Convert empty strings to null for proper API handling
      const payload = {
        userId: parseInt(profileForm.userId),
        departmentId: profileForm.departmentId ? parseInt(profileForm.departmentId) : null,
        jobTitle: profileForm.jobTitle || null,
        employeeId: profileForm.employeeId || null,
        branch: profileForm.branch || null,
        canApproveTransactions: profileForm.canApproveTransactions || false,
        approvalLimit: profileForm.approvalLimit ? parseFloat(profileForm.approvalLimit) : null
      };
      await api.post("/Roles/profiles", payload);
      alert("✅ Profile saved!");
      setShowProfileModal(false);
      loadUsers();
    } catch (err) { alert(err.response?.data?.message || "Failed"); }
  };

  const handleEditRole = async (role) => {
    try {
      const res = await api.get(`/Roles/${role.id}`);
      setSelectedRole(res.data);
      setSelectedPermissions(res.data.permissions.map(p => p.id));
      setShowPermissionsModal(true);
    } catch (err) { alert("Failed to load role"); }
  };

  const handleSavePermissions = async () => {
    try {
      await api.post("/Roles/permissions/assign", { roleId: selectedRole.id, permissionIds: selectedPermissions });
      alert("✅ Permissions updated!");
      setShowPermissionsModal(false);
      loadRoles();
    } catch (err) { alert(err.response?.data?.message || "Failed"); }
  };

  const togglePermission = (permId) => {
    setSelectedPermissions(prev => prev.includes(permId) ? prev.filter(id => id !== permId) : [...prev, permId]);
  };

  const openProfileModal = async (user) => {
    try {
      // Try to fetch existing profile
      const res = await api.get(`/Roles/profiles/${user.userId}`);
      const profile = res.data;
      setProfileForm({
        userId: user.userId,
        departmentId: profile.departmentId || "",
        jobTitle: profile.jobTitle || "",
        employeeId: profile.employeeId || "",
        branch: profile.branch || "",
        canApproveTransactions: profile.canApproveTransactions || false,
        approvalLimit: profile.approvalLimit || ""
      });
    } catch {
      // No existing profile, use defaults
      setProfileForm({
        userId: user.userId,
        departmentId: "",
        jobTitle: "",
        employeeId: "",
        branch: "",
        canApproveTransactions: false,
        approvalLimit: ""
      });
    }
    setSelectedUser(user);
    setShowProfileModal(true);
  };

  const styles = {
    container: { padding: "20px", maxWidth: "1400px", margin: "0 auto", fontFamily: "system-ui, -apple-system, sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "10px" },
    title: { fontSize: "28px", fontWeight: "bold", margin: 0 },
    tabs: { display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" },
    tab: { padding: "10px 20px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "500", transition: "all 0.2s" },
    tabActive: { background: "#1a1a2e", color: "white" },
    tabInactive: { background: "#f3f4f6", color: "#374151" },
    statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "25px" },
    statCard: { background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderTop: "4px solid" },
    statNumber: { fontSize: "32px", fontWeight: "bold", color: "#1a1a2e" },
    statLabel: { color: "#6b7280", fontSize: "14px" },
    card: { background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", marginBottom: "20px" },
    cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { textAlign: "left", padding: "12px", borderBottom: "2px solid #e5e7eb", color: "#6b7280", fontSize: "13px", textTransform: "uppercase" },
    td: { padding: "12px", borderBottom: "1px solid #f3f4f6" },
    btn: { padding: "8px 16px", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500", fontSize: "14px" },
    btnPrimary: { background: "#1a1a2e", color: "white" },
    btnSuccess: { background: "#10b981", color: "white" },
    btnDanger: { background: "#ef4444", color: "white" },
    btnSecondary: { background: "#f3f4f6", color: "#374151" },
    badge: { padding: "4px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "500" },
    modal: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
    modalContent: { background: "white", borderRadius: "12px", padding: "25px", maxWidth: "600px", width: "90%", maxHeight: "80vh", overflow: "auto" },
    modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
    formGroup: { marginBottom: "15px" },
    label: { display: "block", marginBottom: "5px", fontWeight: "500", color: "#374151" },
    input: { width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", boxSizing: "border-box" },
    select: { width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", boxSizing: "border-box" },
    closeBtn: { background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#6b7280" },
    permGrid: { display: "grid", gap: "10px", maxHeight: "400px", overflow: "auto" },
    permModule: { background: "#f9fafb", padding: "15px", borderRadius: "8px" },
    permTitle: { fontWeight: "bold", marginBottom: "10px", color: "#1a1a2e" },
    permItem: { display: "flex", alignItems: "center", gap: "8px", padding: "5px 0" },
    checkbox: { width: "18px", height: "18px" },
    filterRow: { display: "flex", gap: "10px", marginBottom: "15px", flexWrap: "wrap" },
  };

  if (loading) return <div style={{ padding: "50px", textAlign: "center" }}>Loading...</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>👥 Role Management</h1>
        <button onClick={() => navigate("/admin")} style={{ ...styles.btn, ...styles.btnSecondary }}>← Back to Admin</button>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {["dashboard", "roles", "departments", "users", "permissions"].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : styles.tabInactive) }}>
            {tab === "dashboard" && "📊 Dashboard"}
            {tab === "roles" && "🎭 Roles"}
            {tab === "departments" && "🏢 Departments"}
            {tab === "users" && "👤 Users"}
            {tab === "permissions" && "🔐 Permissions"}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === "dashboard" && dashboard && (
        <>
          <div style={styles.statsGrid}>
            <div style={{ ...styles.statCard, borderTopColor: "#3b82f6" }}>
              <div style={styles.statNumber}>{dashboard.totalRoles}</div>
              <div style={styles.statLabel}>Total Roles</div>
            </div>
            <div style={{ ...styles.statCard, borderTopColor: "#10b981" }}>
              <div style={styles.statNumber}>{dashboard.totalDepartments}</div>
              <div style={styles.statLabel}>Departments</div>
            </div>
            <div style={{ ...styles.statCard, borderTopColor: "#8b5cf6" }}>
              <div style={styles.statNumber}>{dashboard.totalPermissions}</div>
              <div style={styles.statLabel}>Permissions</div>
            </div>
            <div style={{ ...styles.statCard, borderTopColor: "#f59e0b" }}>
              <div style={styles.statNumber}>{dashboard.usersWithRoles}</div>
              <div style={styles.statLabel}>Users with Roles</div>
            </div>
            <div style={{ ...styles.statCard, borderTopColor: "#ef4444" }}>
              <div style={styles.statNumber}>{dashboard.usersWithoutRoles}</div>
              <div style={styles.statLabel}>Users without Roles</div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <div style={styles.card}>
              <h3>🎭 Roles by Department</h3>
              {roles.map(role => (
                <div key={role.id} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <div>
                    <strong>{role.name}</strong>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>{role.department || "No department"}</div>
                  </div>
                  <span style={{ ...styles.badge, background: "#e0e7ff", color: "#3730a3" }}>{role.userCount} users</span>
                </div>
              ))}
            </div>

            <div style={styles.card}>
              <h3>🏢 Department Summary</h3>
              {dashboard.departmentSummaries?.map(dept => (
                <div key={dept.departmentId} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <strong>{dept.departmentName}</strong>
                  <span style={{ ...styles.badge, background: "#d1fae5", color: "#065f46" }}>{dept.userCount} staff</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Roles Tab */}
      {activeTab === "roles" && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3>🎭 Roles</h3>
            <button onClick={() => { setShowRoleModal(true); loadPermissions(); }} style={{ ...styles.btn, ...styles.btnPrimary }}>+ Create Role</button>
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Role Name</th>
                <th style={styles.th}>Department</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Users</th>
                <th style={styles.th}>Permissions</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map(role => (
                <tr key={role.id}>
                  <td style={styles.td}><strong>{role.name}</strong></td>
                  <td style={styles.td}>{role.department || "-"}</td>
                  <td style={styles.td}>{role.description || "-"}</td>
                  <td style={styles.td}><span style={{ ...styles.badge, background: "#e0e7ff", color: "#3730a3" }}>{role.userCount}</span></td>
                  <td style={styles.td}><span style={{ ...styles.badge, background: "#fef3c7", color: "#92400e" }}>{role.permissionCount}</span></td>
                  <td style={styles.td}>
                    <button onClick={() => handleEditRole(role)} style={{ ...styles.btn, ...styles.btnSecondary, marginRight: "5px" }}>Edit Permissions</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Departments Tab */}
      {activeTab === "departments" && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3>🏢 Departments</h3>
            <button onClick={() => setShowDeptModal(true)} style={{ ...styles.btn, ...styles.btnPrimary }}>+ Create Department</button>
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Code</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Staff Count</th>
              </tr>
            </thead>
            <tbody>
              {departments.map(dept => (
                <tr key={dept.id}>
                  <td style={styles.td}><strong>{dept.name}</strong></td>
                  <td style={styles.td}>{dept.code || "-"}</td>
                  <td style={styles.td}>{dept.description || "-"}</td>
                  <td style={styles.td}><span style={{ ...styles.badge, background: "#d1fae5", color: "#065f46" }}>{dept.userCount}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === "users" && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3>👤 User Roles & Profiles</h3>
            <button onClick={() => setShowAssignRoleModal(true)} style={{ ...styles.btn, ...styles.btnSuccess }}>+ Assign Role</button>
          </div>
          <div style={styles.filterRow}>
            <select style={{ ...styles.select, width: "200px" }} value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select style={{ ...styles.select, width: "200px" }} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">All Roles</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Department</th>
                <th style={styles.th}>Job Title</th>
                <th style={styles.th}>Roles</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.userId}>
                  <td style={styles.td}><strong>{user.fullName}</strong></td>
                  <td style={styles.td}>{user.email}</td>
                  <td style={styles.td}>{user.department || "-"}</td>
                  <td style={styles.td}>{user.jobTitle || "-"}</td>
                  <td style={styles.td}>
                    {user.roles?.length > 0 ? user.roles.map(r => (
                      <span key={r.id} style={{ ...styles.badge, background: "#e0e7ff", color: "#3730a3", marginRight: "5px" }}>
                        {r.name}
                        <button onClick={() => handleRemoveRole(user.userId, r.id)} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", marginLeft: "5px" }}>×</button>
                      </span>
                    )) : <span style={{ color: "#9ca3af" }}>No roles</span>}
                  </td>
                  <td style={styles.td}>
                    <button onClick={() => openProfileModal(user)} style={{ ...styles.btn, ...styles.btnSecondary }}>Edit Profile</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Permissions Tab */}
      {activeTab === "permissions" && (
        <div style={styles.card}>
          <h3>🔐 All Permissions by Module</h3>
          <div style={styles.permGrid}>
            {permissions.map(group => (
              <div key={group.module} style={styles.permModule}>
                <div style={styles.permTitle}>{group.module}</div>
                {group.permissions.map(p => (
                  <div key={p.id} style={styles.permItem}>
                    <span style={{ ...styles.badge, background: "#f3f4f6", color: "#374151" }}>{p.category}</span>
                    <span>{p.name}</span>
                    <span style={{ color: "#9ca3af", fontSize: "12px" }}>({p.code})</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Role Modal */}
      {showRoleModal && (
        <div style={styles.modal} onClick={() => setShowRoleModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>🎭 Create Role</h3>
              <button style={styles.closeBtn} onClick={() => setShowRoleModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateRole}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Role Name *</label>
                <input style={styles.input} required value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} placeholder="e.g., Teller, Manager" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Department</label>
                <select style={styles.select} value={roleForm.department} onChange={(e) => setRoleForm({ ...roleForm, department: e.target.value })}>
                  <option value="">Select Department</option>
                  <option value="Operations">Operations</option>
                  <option value="Compliance">Compliance</option>
                  <option value="Customer Service">Customer Service</option>
                  <option value="Finance">Finance</option>
                  <option value="IT">IT</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <input style={styles.input} value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} placeholder="Brief description of this role" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Permissions</label>
                <div style={{ ...styles.permGrid, maxHeight: "200px", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "10px" }}>
                  {permissions.map(group => (
                    <div key={group.module}>
                      <strong style={{ fontSize: "12px", color: "#6b7280" }}>{group.module}</strong>
                      {group.permissions.map(p => (
                        <label key={p.id} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", padding: "3px 0" }}>
                          <input type="checkbox" checked={selectedPermissions.includes(p.id)} onChange={() => togglePermission(p.id)} />
                          <span style={{ fontSize: "13px" }}>{p.name}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <button type="submit" style={{ ...styles.btn, ...styles.btnPrimary, width: "100%", padding: "12px" }}>Create Role</button>
            </form>
          </div>
        </div>
      )}

      {/* Create Department Modal */}
      {showDeptModal && (
        <div style={styles.modal} onClick={() => setShowDeptModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>🏢 Create Department</h3>
              <button style={styles.closeBtn} onClick={() => setShowDeptModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateDept}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Department Name *</label>
                <input style={styles.input} required value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Code</label>
                <input style={styles.input} value={deptForm.code} onChange={(e) => setDeptForm({ ...deptForm, code: e.target.value })} placeholder="e.g., OPS, FIN" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <input style={styles.input} value={deptForm.description} onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })} />
              </div>
              <button type="submit" style={{ ...styles.btn, ...styles.btnPrimary, width: "100%", padding: "12px" }}>Create Department</button>
            </form>
          </div>
        </div>
      )}

      {/* Assign Role Modal */}
      {showAssignRoleModal && (
        <div style={styles.modal} onClick={() => setShowAssignRoleModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>👤 Assign Role to User</h3>
              <button style={styles.closeBtn} onClick={() => setShowAssignRoleModal(false)}>×</button>
            </div>
            <form onSubmit={handleAssignRole}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Select User *</label>
                <select style={styles.select} required value={assignForm.userId} onChange={(e) => setAssignForm({ ...assignForm, userId: e.target.value })}>
                  <option value="">Choose user...</option>
                  {allUsers.map(u => <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>)}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Select Role *</label>
                <select style={styles.select} required value={assignForm.roleId} onChange={(e) => setAssignForm({ ...assignForm, roleId: e.target.value })}>
                  <option value="">Choose role...</option>
                  {roles.map(r => <option key={r.id} value={r.id}>{r.name} ({r.department})</option>)}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                  <input type="checkbox" checked={assignForm.isPrimary} onChange={(e) => setAssignForm({ ...assignForm, isPrimary: e.target.checked })} />
                  Set as Primary Role
                </label>
              </div>
              <button type="submit" style={{ ...styles.btn, ...styles.btnSuccess, width: "100%", padding: "12px" }}>Assign Role</button>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Profile Modal */}
      {showProfileModal && (
        <div style={styles.modal} onClick={() => setShowProfileModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>👤 Edit Profile - {selectedUser?.fullName}</h3>
              <button style={styles.closeBtn} onClick={() => setShowProfileModal(false)}>×</button>
            </div>
            <form onSubmit={handleSaveProfile}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Department</label>
                  <select style={styles.select} value={profileForm.departmentId} onChange={(e) => setProfileForm({ ...profileForm, departmentId: e.target.value })}>
                    <option value="">Select Department</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Job Title</label>
                  <input style={styles.input} value={profileForm.jobTitle} onChange={(e) => setProfileForm({ ...profileForm, jobTitle: e.target.value })} placeholder="e.g., Senior Teller" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Employee ID</label>
                  <input style={styles.input} value={profileForm.employeeId} onChange={(e) => setProfileForm({ ...profileForm, employeeId: e.target.value })} placeholder="Staff ID" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Branch</label>
                  <input style={styles.input} value={profileForm.branch} onChange={(e) => setProfileForm({ ...profileForm, branch: e.target.value })} placeholder="Branch location" />
                </div>
              </div>
              <button type="submit" style={{ ...styles.btn, ...styles.btnPrimary, width: "100%", padding: "12px", marginTop: "15px" }}>Save Profile</button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Role Permissions Modal */}
      {showPermissionsModal && selectedRole && (
        <div style={styles.modal} onClick={() => setShowPermissionsModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>🔐 Edit Permissions - {selectedRole.name}</h3>
              <button style={styles.closeBtn} onClick={() => setShowPermissionsModal(false)}>×</button>
            </div>
            <div style={{ ...styles.permGrid, maxHeight: "400px", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "15px", marginBottom: "15px" }}>
              {permissions.map(group => (
                <div key={group.module} style={styles.permModule}>
                  <div style={styles.permTitle}>{group.module}</div>
                  {group.permissions.map(p => (
                    <label key={p.id} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", padding: "5px 0" }}>
                      <input type="checkbox" style={styles.checkbox} checked={selectedPermissions.includes(p.id)} onChange={() => togglePermission(p.id)} />
                      <span>{p.name}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
            <button onClick={handleSavePermissions} style={{ ...styles.btn, ...styles.btnPrimary, width: "100%", padding: "12px" }}>
              Save Permissions ({selectedPermissions.length} selected)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
